/**
 * settings-branding.js
 *
 * @author:          Fabian Tollenaar <fabian> <fabian@decipher.industries>
 * @date:            2018-02-12 22:56
 * @copyright:       Fabian Tollenaar/Decipher Industries (c) 2018. All rights reserved.
 * @license:         UNLICENSED
 * @modified:        2018-10-16 22:44
 */

import './styles.styl'
import React from 'react'
import Editor from 'react-simple-code-editor'
import { highlight, languages } from 'prismjs/components/prism-core'
import Color from '../../color'
import fonts from '../../../fonts.json'
import Uploader from '../../../lib/upload'
import { locales } from '../../../locale-codes'

import 'prismjs/components/prism-css'
import 'prismjs/themes/prism.css'

export default class SettingsBranding extends React.Component {
  constructor(props) {
    super(props)

    let state = {
      stringsFileSubtext: '',
      stringsFileSubtextColor: 'red',
      stringsFileName: '',
      strings: {},
      faqFileSubtext: '',
      faqFileSubtextColor: 'red',
      faqFileName: '',
      faq: [],
      primaryFont: 'Roboto',
      secondaryFont: 'Roboto',
      generalTerms: '',
      privacyStatement: '',
      logo: 'http://users.iloyaltyserver.com/iloyalty.png',
      sendPackageSlip: false,
      packageSlipExtraText: '',
      addColourMappingValue: '',
      mixinCss: '',
      colours: {
        primary_basis: '#F37B03',
        primary_basis_fg: '#FFFFFF',
        primary_shadow: '#C96400',
        primary_shadow_fg: '#FFFFFF',
        primary_light: '#F29231',
        primary_light_fg: '#000000',
        primary_dull: '#FFDFBF',
        primary_dull_fg: '#000000',

        secondary_basis: '#951C81',
        secondary_basis_fg: '#ffffff',
        secondary_shadow: '#7B3570',
        secondary_shadow_fg: '#ffffff',
        secondary_light: '#AC499B',
        secondary_light_fg: '#000000',
        secondary_dull: '#BD8BCA',
        secondary_dull_fg: '#000000',

        tertiary_basis: '#9567A2',
        tertiary_basis_fg: '#FFFFFF',
        tertiary_shadow: '#785681',
        tertiary_shadow_fg: '#FFFFFF',
        tertiary_light: '#B08BBA',
        tertiary_light_fg: '#000000',
        tertiary_dull: '#DCC9E1',
        tertiary_dull_fg: '#000000',

        quaternary_basis: '#000000',
        quaternary_basis_fg: '#FFFFFF',
        quaternary_shadow: '#4A4A4A',
        quaternary_shadow_fg: '#FFFFFF',
        quaternary_light: '#979797',
        quaternary_light_fg: '#000000',
        quaternary_dull: '#CCCCCC',
        quaternary_dull_fg: '#000000',
      },
      colourMappings: {
        'header-0': 'secondary_basis',
        'header-1': 'white',
        'header-2': 'primary_basis',
        'footer-0': 'primary_basis',
        'footer-login': 'secondary_basis',
        'button-primary': 'primary_basis',
        'title-h1': 'secondary_basis',
        'title-h2': 'secondary_basis',
        'title-h3': 'secondary_basis',
        'title-h4': 'secondary_basis',
        a: 'primary_basis',
        'a:link': 'primary_basis',
        'a:visited': 'primary_basis',
        'a:hover': 'secondary_basis',
        'a:active': 'secondary_basis',
        'progress-label-text-colour': 'quaternary_dull',
        'input-range__track--background': 'quaternary_dull',
        'input-range__track--active': 'primary_basis',
        'input-range__slider': 'primary_basis',
        'input-range__label--min': 'quaternary_dull_fg',
        'input-range__label--max': 'quaternary_dull_fg',
        'input-range__label--value': 'quaternary_dull_fg',
        'search-form-button': 'black',
        'search-form-icon': 'white',
        'main-menu-link_hover': 'black',
        'main-menu-link': 'black',
        'sub-menu-link_hover': 'black',
        'sub-menu-link': 'black',
        'shopping-cart-link': 'black',
        'shopping-cart-icon': 'black',
        'shopping-cart-badge': 'red',
      },
    }

    if (props.data !== null) {
      props.data.colours = {
        ...state.colours,
        ...props.data.colours,
      }

      props.data.colourMappings = {
        ...state.colourMappings,
        ...props.data.colourMappings,
      }

      state = {
        ...state,
        ...props.data,
      }
    }

    const langs = props.languages && Array.isArray(props.languages.secondaryLanguages) ? props.languages.secondaryLanguages : []

    for (const loc of langs) {
      if (props.data.hasOwnProperty(`altLangFile-${loc}`)) {
        state = {
          ...state,
          [`altLangFile-${loc}`]: props.data[`altLangFile-${loc}`],
          [`altLangFileSubtext-${loc}`]: `Ingeladen: ${props.data[`altLangFile-${loc}`].length} regels`,
          [`altLangFileSubtextColor-${loc}`]: `green`,
        }
      }

      if (props.data.hasOwnProperty(`altFaqFile-${loc}`)) {
        state = {
          ...state,
          [`altFaqFile-${loc}`]: props.data[`altFaqFile-${loc}`],
          [`altFaqFileSubtext-${loc}`]: `Ingeladen: ${props.data[`altFaqFile-${loc}`].length} regels`,
          [`altFaqFileSubtextColor-${loc}`]: `green`,
        }
      }
    }

    this.state = state
  }

  slugify(text) {
    return String(text)
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w-]+/g, '') // Remove all non-word chars
      .replace(/--+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '') // Trim - from end of text
  }

  saveAndToggle(index) {
    this.props.save({ ...this.state })
    this.props.toggleSection(index)
  }

  saveAndNext(index) {
    this.props.save({ ...this.state })
    this.props.nextSection(index)
  }

  parseCSV (data, callback) {
    let comma = 0
    let semicolon = 0
    let seperator = null

    data = data
      .split('\n')
      .map((line) => String(line).replace('\r', '').trim())
      .filter((line) => line !== '')

    data.forEach((line) => {
      if (line.indexOf(';') !== -1) {
        semicolon += 1
      }

      if (line.indexOf(',') !== -1) {
        comma += 1
      }
    })

    if (comma >= data.length) {
      seperator = ','
    }

    if (semicolon >= data.length) {
      seperator = ';'
    }

    if (data.length === 0 || seperator === null) {
      return callback(new Error('Controleer de inhoud van het taalbestand: het is niet gelukt om de data te lezen.'))
    }

    return callback(undefined, {
      seperator,
      data,
      lines: data.filter((line) => (line && line.trim() !== '')).map((line) => line.split(seperator))
    })
  }

  handleFile(kind, input, handleLines) {
    this.parseCSV(input, (err, { seperator, data }) => {
      if (err) {
        this.setState({ [`${kind}FileSubtext`]: err.message, })
        return
      }

      return handleLines(data, seperator)
    })
  }

  readFile (e, callback) {
    if (!window.hasOwnProperty('FileReader')) {
      return window.alert(
        'Het inlezen van bestanden wordt niet ondersteund door uw browser. Gebruik s.v.p. Google Chrome, Safari of Firefox.'
      )
    }

    let file = e.target.files

    if (file.length === 0) {
      return callback(new Error('Selecteer s.v.p. een valide CSV bestand.'))
    }

    if (file.length > 0) {
      file = file[0]
    }

    if (String(file.name).toLowerCase().indexOf('csv') === -1) {
      return callback(new Error(`Het geselecteerde bestand is geen (valide) CSV bestand.`))
    }

    try {
      const reader = new window.FileReader()
      reader.addEventListener('load', (e) => callback(null, {
        data: e.target.result,
        file
      }))
      reader.readAsText(file)
    } catch (e) {
      callback(e)
    }
  }

  loadFile(kind, e, handler) {
    this.readFile(e, (err, result) => {
      if (err) {
        this.setState({ [`${kind}FileSubtext`]: err.message })
        this.setState({ [`${kind}FileSubtextColor`]: 'red' })
        return
      }

      this.setState({ [`${kind}FileName`]: result.file.name })
      handler(result.data)
    })
  }

  handleLanguageFile(data) {
    this.handleFile('strings', data, (lines, seperator) => {
      const strings = lines.reduce((strings, line) => {
        line = line.split(seperator)
        strings[String(line[0]).trim().replace(/\./g, '+')] = String(line[1]).trim()
        return strings
      }, {})

      this.setState({
        strings,
        stringsFileSubtextColor: 'green',
        stringsFileSubtext: `Ingeladen: ${Object.keys(strings).length} taalregels uit bestand "${
          this.state.stringsFileName
        }"`,
      })
    })
  }

  loadLanguageFile(e) {
    this.loadFile('strings', e, this.handleLanguageFile.bind(this))
  }

  loadAltFile (kind, loc, e) {
    const handleError = (err) => {
      const name = kind === 'faq' ? 'Faq' : 'Lang'
      console.log(`${name} error: ${err.message}`)
      return this.setState({
        [`alt${name}File-${loc}`]: undefined,
        [`alt${name}FileSubtext-${loc}`]: err.message,
        [`alt${name}FileSubtextColor-${loc}`]: 'red'
      })
    }

    this.readFile(e, (err, result) => {
      if (err) {
        return handleError(err)
      }

      this.parseCSV(result.data, (err, { lines }) => {
        if (err) {
          return handleError(err)
        }

        const name = kind === 'faq' ? 'Faq' : 'Lang'
        const newState = {
          [`alt${name}File-${loc}`]: lines,
          [`alt${name}Subtext-${loc}`]: `Ingeladen: ${lines.length}`,
          [`alt${name}SubtextColor-${loc}`]: 'green',
        }

        console.log(`Setting state...`, newState)
        this.setState(newState)
      })
    })
  }

  handleFaqFile(data) {
    this.handleFile('faq', data, (lines, seperator) => {
      let count = 0
      const sections = {}

      lines.forEach((line) => {
        line = line
          .split(seperator)
          .map((col) => String(col).trim())
          .filter((col) => col !== '')

        if (line.length !== 3) {
          return
        }

        const section = line[0]
        const question = line[1]
        const answer = line[2]

        if (typeof sections[section] === 'undefined') {
          sections[section] = {
            title: section,
            questions: [],
          }
        }

        count += 1
        sections[section].questions.push({
          q: question.replace(/"/g, ''),
          a: answer.replace(/"/g, ''),
        })
      })

      this.setState({
        faq: Object.keys(sections).map((section) => sections[section]),
        faqFileSubtextColor: 'green',
        faqFileSubtext: `Ingeladen: ${count} taalregels uit bestand "${this.state.faqFileName}"`,
      })
    })
  }

  loadFaqFile(e) {
    this.loadFile('faq', e, this.handleFaqFile.bind(this))
  }

  handleColorChange(colour, isFg) {
    const colours = Object.assign({}, this.state.colours)

    if (isFg === true) {
      colours[`${colour.key}_fg`] = colour.value
    } else {
      colours[colour.key] = colour.value
    }

    this.setState({ colours })
  }

  uploadFile(e, callback) {
    if (typeof callback !== 'function') {
      callback = () => {}
    }

    let file = e.target.files

    if (file.length === 0) {
      return
    }

    if (file.length > 0) {
      file = file[0]
    }

    const uploader = new Uploader(file, this.props.user)
    uploader
      .upload()
      .then((result) => {
        console.log('[Uploader] Uploaded file:', result)
        callback(result.url)
      })
      .catch((err) => {
        console.error(`[Uploader] Error uploading file: ${err.message}`)
      })
  }

  downloadCSVFile(kind = 'i18n', filename = `${Date.now()}.csv`) {
    const { faq, strings } = this.state

    switch (kind) {
      case 'faq':
        return this.generateAndDownloadCSV('faq', faq, filename)

      default:
        return this.generateAndDownloadCSV('i18n', strings, filename)
    }
  }

  generateAndDownloadCSV(kind, data, filename) {
    // let headers = []
    let lines = []

    if (kind === 'faq') {
      // headers = ['section', 'question', 'answer']
      data.forEach((faqItem) => {
        faqItem.questions.forEach((qa) => {
          lines.push([faqItem.title, qa.q, qa.a])
        })
      })
    } else {
      Object.keys(data).forEach((key) => {
        // headers = ['key', 'value']
        const value = data[key]
        lines.push([key, value])
      })
    }

    let txt = `data:text/csv;charset=utf-8,`
    lines.forEach((line) => (txt += `${line.join(';')}\n`))

    const encoded = encodeURI(txt)
    const link = document.createElement('a')

    link.setAttribute('href', encoded)
    link.setAttribute('download', filename)

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  addColourMapping() {
    const { addColourMappingValue, colourMappings } = this.state

    if (typeof addColourMappingValue !== 'string' || addColourMappingValue.trim() === '') {
      return
    }

    colourMappings[addColourMappingValue] = 'primary_basis'
    this.setState({
      colourMappings,
      addColourMappingValue: '',
    })
  }

  setColourMapping(key, value) {
    const colourMappings = {
      ...this.state.colourMappings,
    }

    colourMappings[key] = value
    this.setState({ colourMappings })
  }

  colourMappingKeyLabel(key) {
    return key
  }

  render() {
    const index = this.props.index;
    const lang = this.props.languages;

    return (
      <section
        key={index}
        className={['section', this.props.open ? 'section-open' : 'section-closed'].join(' ')}
      >
        <div className="row section-title">
          <div className="col-10">
            <h3>Branding</h3>
          </div>

          <div className="col-2 text-align-right">
            <a href="#" onClick={() => this.saveAndToggle(index)} className="edit-link">
              {this.props.open ? 'Sluiten' : 'Bewerken'}
            </a>
          </div>
        </div>

        <div className="row section-content">
          <div className="col-12 col-lg-10">
            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Taalbestand</label>
              </div>

              <div className="col-12 col-sm-8">
                <div>
                  <input
                    type="file"
                    className="form-control-file form-control-sm"
                    placeholder="Selecteer een CSV bestand."
                    onChange={(e) => this.loadLanguageFile(e)}
                  />
                  <em
                    className="small"
                    style={{ marginLeft: '1em', color: this.state.stringsFileSubtextColor }}
                  >
                    {this.state.stringsFileSubtext}
                  </em>
                  {this.state.stringsFileSubtextColor === 'green' && (
                    <em className="small" style={{ marginLeft: '1em' }}>
                      <a onClick={() => this.downloadCSVFile('i18n', `i18n_${Date.now()}.csv`)}>
                        Downloaden
                      </a>
                    </em>
                  )}
                </div>
              </div>
            </div>

            {(Array.isArray(lang.secondaryLanguages) ? lang.secondaryLanguages : []).map((loc) => {
              const locale = locales.find((candidate) => (candidate.tag === loc))

              if (!locale) {
                return <span key={`langfile-${loc}`} />
              }

              return (
                <div className="row subsection" key={`langfile-${loc}`}>
                  <div className="col-12 col-sm-4">
                    <label className="form-label" style={{ paddingLeft: '2em' }}>{locale.name}</label>
                  </div>

                  <div className="col-12 col-sm-8">
                    <input
                      type="file"
                      className="form-control-file form-control-sm"
                      placeholder="Selecteer een CSV bestand."
                      onChange={(e) => this.loadAltFile('lang', loc, e)}
                    />
                    <em
                      className="small"
                      style={{ marginLeft: '1em', color: this.state[`altLangFileSubtextColor-${loc}`] || 'red' }}
                    >
                      {this.state[`altLangFileSubtext-${loc}`] || ''}
                    </em>
                    {this.state[`altLangFileSubtextColor-${loc}`] === 'green' && (
                      <em className="small" style={{ marginLeft: '1em' }}>
                        <a onClick={() => this.downloadCSVFile(`altLangFile-${loc}`, `altlang_${loc}_${Date.now()}.csv`)}>
                          Downloaden
                        </a>
                      </em>
                    )}
                  </div>
                </div>
              )
            })}

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">FAQ</label>
              </div>

              <div className="col-12 col-sm-8">
                <div>
                  <input
                    type="file"
                    className="form-control-file form-control-sm"
                    placeholder="Selecteer een CSV bestand."
                    onChange={(e) => this.loadFaqFile(e)}
                  />
                  <em
                    className="small"
                    style={{ marginLeft: '1em', color: this.state.faqFileSubtextColor }}
                  >
                    {this.state.faqFileSubtext}
                  </em>
                  {this.state.faqFileSubtextColor === 'green' && (
                    <em className="small" style={{ marginLeft: '1em' }}>
                      <a onClick={() => this.downloadCSVFile('faq', `faq_${Date.now()}.csv`)}>
                        Downloaden
                      </a>
                    </em>
                  )}
                </div>
              </div>
            </div>

            {(Array.isArray(lang.secondaryLanguages) ? lang.secondaryLanguages : []).map((loc) => {
              const locale = locales.find((candidate) => (candidate.tag === loc))

              if (!locale) {
                return <span key={`faq-${loc}`} />
              }

              return (
                <div className="row subsection" key={`faq-${loc}`}>
                  <div className="col-12 col-sm-4">
                    <label className="form-label" style={{ paddingLeft: '2em' }}>{locale.name}</label>
                  </div>

                  <div className="col-12 col-sm-8">
                    <input
                      type="file"
                      className="form-control-file form-control-sm"
                      placeholder="Selecteer een CSV bestand."
                      onChange={(e) => this.loadAltFile('faq', loc, e)}
                    />
                    <em
                      className="small"
                      style={{ marginLeft: '1em', color: this.state[`altFaqFileSubtextColor-${loc}`] || 'red' }}
                    >
                      {this.state[`altFaqFileSubtext-${loc}`] || ''}
                    </em>
                    {this.state[`altFaqFileSubtextColor-${loc}`] === 'green' && (
                      <em className="small" style={{ marginLeft: '1em' }}>
                        <a onClick={() => this.downloadCSVFile(`altFaqFile-${loc}`, `altfaq_${loc}_${Date.now()}.csv`)}>
                          Downloaden
                        </a>
                      </em>
                    )}
                  </div>
                </div>
              )
            })}

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Kleuren</label>
              </div>

              <div className="col-12 col-sm-8">
                <table className="table">
                  <tbody>
                    <tr>
                      <td />
                      <td>Basis</td>
                      <td>Schaduw</td>
                      <td>Licht</td>
                      <td>Dof</td>
                    </tr>

                    <tr>
                      <td>Primair</td>
                      <td>
                        <Color
                          name="primary_basis"
                          bg={this.state.colours['primary_basis']}
                          fg={this.state.colours['primary_basis_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="primary_shadow"
                          bg={this.state.colours['primary_shadow']}
                          fg={this.state.colours['primary_shadow_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="primary_light"
                          bg={this.state.colours['primary_light']}
                          fg={this.state.colours['primary_light_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="primary_dull"
                          bg={this.state.colours['primary_dull']}
                          fg={this.state.colours['primary_dull_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>Secundair</td>
                      <td>
                        <Color
                          name="secondary_basis"
                          bg={this.state.colours['secondary_basis']}
                          fg={this.state.colours['secondary_basis_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="secondary_shadow"
                          bg={this.state.colours['secondary_shadow']}
                          fg={this.state.colours['secondary_shadow_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="secondary_light"
                          bg={this.state.colours['secondary_light']}
                          fg={this.state.colours['secondary_light_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="secondary_dull"
                          bg={this.state.colours['secondary_dull']}
                          fg={this.state.colours['secondary_dull_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>Tertiar</td>
                      <td>
                        <Color
                          name="tertiary_basis"
                          bg={this.state.colours['tertiary_basis']}
                          fg={this.state.colours['tertiary_basis_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="tertiary_shadow"
                          bg={this.state.colours['tertiary_shadow']}
                          fg={this.state.colours['tertiary_shadow_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="tertiary_light"
                          bg={this.state.colours['tertiary_light']}
                          fg={this.state.colours['tertiary_light_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="tertiary_dull"
                          bg={this.state.colours['tertiary_dull']}
                          fg={this.state.colours['tertiary_dull_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                    </tr>

                    <tr>
                      <td>Quartair</td>
                      <td>
                        <Color
                          name="quaternary_basis"
                          bg={this.state.colours['quaternary_basis']}
                          fg={this.state.colours['quaternary_basis_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="quaternary_shadow"
                          bg={this.state.colours['quaternary_shadow']}
                          fg={this.state.colours['quaternary_shadow_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="quaternary_light"
                          bg={this.state.colours['quaternary_light']}
                          fg={this.state.colours['quaternary_light_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                      <td>
                        <Color
                          name="quaternary_dull"
                          bg={this.state.colours['quaternary_dull']}
                          fg={this.state.colours['quaternary_dull_fg']}
                          onChange={(color, isFg) => this.handleColorChange(color, isFg)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Kleur mappings</label>
              </div>

              <div className="col-12 col-sm-8">
                <table className="table">
                  <thead>
                    <tr>
                      <th>UI element</th>
                      <th>Kleur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(this.state.colourMappings).map((key) => (
                      <tr key={key}>
                        <td>{this.colourMappingKeyLabel(key)}</td>
                        <td>
                          <select
                            className="form-control"
                            value={this.state.colourMappings[key]}
                            onChange={(e) => this.setColourMapping(key, e.target.value)}
                          >
                            <option value="white">Wit</option>
                            <option value="black">Zwart</option>
                            <option value="red">Rood</option>

                            <option value="primary_basis">Primair, basis (achtergrondkleur)</option>
                            <option value="primary_shadow">
                              Primair, schaduw (achtergrondkleur)
                            </option>
                            <option value="primary_light">Primair, licht (achtergrondkleur)</option>
                            <option value="primary_dull">Primair, dof (achtergrondkleur)</option>
                            <option value="primary_basis_fg">Primair, basis (tekstkleur)</option>
                            <option value="primary_shadow_fg">Primair, schaduw (tekstkleur)</option>
                            <option value="primary_light_fg">Primair, licht (tekstkleur)</option>
                            <option value="primary_dull_fg">Primair, dof (tekstkleur)</option>

                            <option value="secondary_basis">
                              Secundair, basis (achtergrondkleur)
                            </option>
                            <option value="secondary_shadow">
                              Secundair, schaduw (achtergrondkleur)
                            </option>
                            <option value="secondary_light">
                              Secundair, licht (achtergrondkleur)
                            </option>
                            <option value="secondary_dull">
                              Secundair, dof (achtergrondkleur)
                            </option>
                            <option value="secondary_basis_fg">
                              Secundair, basis (tekstkleur)
                            </option>
                            <option value="secondary_shadow_fg">
                              Secundair, schaduw (tekstkleur)
                            </option>
                            <option value="secondary_light_fg">
                              Secundair, licht (tekstkleur)
                            </option>
                            <option value="secondary_dull_fg">Secundair, dof (tekstkleur)</option>

                            <option value="tertiary_basis">
                              Tertiar, basis (achtergrondkleur)
                            </option>
                            <option value="tertiary_shadow">
                              Tertiar, schaduw (achtergrondkleur)
                            </option>
                            <option value="tertiary_light">
                              Tertiar, licht (achtergrondkleur)
                            </option>
                            <option value="tertiary_dull">Tertiar, dof (achtergrondkleur)</option>
                            <option value="tertiary_basis_fg">Tertiar, basis (tekstkleur)</option>
                            <option value="tertiary_shadow_fg">
                              Tertiar, schaduw (tekstkleur)
                            </option>
                            <option value="tertiary_light_fg">Tertiar, licht (tekstkleur)</option>
                            <option value="tertiary_dull_fg">Tertiar, dof (tekstkleur)</option>

                            <option value="quaternary_basis">
                              Quartair, basis (achtergrondkleur)
                            </option>
                            <option value="quaternary_shadow">
                              Quartair, schaduw (achtergrondkleur)
                            </option>
                            <option value="quaternary_light">
                              Quartair, licht (achtergrondkleur)
                            </option>
                            <option value="quaternary_dull">
                              Quartair, dof (achtergrondkleur)
                            </option>
                            <option value="quaternary_basis_fg">
                              Quartair, basis (tekstkleur)
                            </option>
                            <option value="quaternary_shadow_fg">
                              Quartair, schaduw (tekstkleur)
                            </option>
                            <option value="quaternary_light_fg">
                              Quartair, licht (tekstkleur)
                            </option>
                            <option value="quaternary_dull_fg">Quartair, dof (tekstkleur)</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2">
                        <label className="form-label">Nieuwe mapping:</label>
                        <input
                          type="text"
                          placeholder="UI element"
                          className="form-control"
                          value={this.state.addColourMappingValue}
                          onChange={(e) => this.setState({ addColourMappingValue: e.target.value })}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          style={{ marginTop: '0.5em' }}
                          onClick={() => this.addColourMapping()}
                        >
                          Toevoegen
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Primair lettertype</label>
              </div>

              <div className="col-12 col-sm-8">
                <div className="form-group">
                  <select
                    className="form-control"
                    value={this.state.primaryFont}
                    onChange={(e) => this.setState({ primaryFont: e.target.value })}
                  >
                    {fonts.map((font, index) => (
                      <option key={index} value={font}>
                        {font}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Secundair lettertype</label>
              </div>

              <div className="col-12 col-sm-8">
                <div className="form-group">
                  <select
                    className="form-control"
                    value={this.state.secondaryFont}
                    onChange={(e) => this.setState({ secondaryFont: e.target.value })}
                  >
                    {fonts.map((font, index) => (
                      <option key={index} value={font}>
                        {font}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Logo</label>
              </div>

              <div className="col-12 col-sm-8">
                <div>
                  <input
                    type="file"
                    className="form-control-file form-control-sm"
                    placeholder="Logo (816px x 186px / aspact ratio: 136:31)"
                    onChange={(e) => this.uploadFile(e, (url) => this.setState({ logo: url }))}
                  />
                  <br />
                  <div
                    className="logo-example"
                    style={{ backgroundImage: `url(${this.state.logo})` }}
                  />
                </div>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Algemene Voorwaarden</label>
              </div>

              <div className="col-12 col-sm-8">
                <div>
                  <input
                    type="file"
                    className="form-control-file form-control-sm"
                    placeholder="Algemene Voorwaarden (PDF)"
                    onChange={(e) =>
                      this.uploadFile(e, (url) => this.setState({ generalTerms: url }))
                    }
                  />
                  {this.state.generalTerms === '' ? null : (
                    <a
                      style={{ marginLeft: '.5em' }}
                      target="_blank"
                      href={this.state.generalTerms}
                    >
                      Huidige Algemene Voorwaarden (klik om te downloaden)
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Privacy Statement</label>
              </div>

              <div className="col-12 col-sm-8">
                <div>
                  <input
                    type="file"
                    className="form-control-file form-control-sm"
                    placeholder="Privacy Statement (PDF)"
                    onChange={(e) =>
                      this.uploadFile(e, (url) => this.setState({ privacyStatement: url }))
                    }
                  />
                  {this.state.privacyStatement === '' ? null : (
                    <a
                      style={{ marginLeft: '.5em' }}
                      target="_blank"
                      href={this.state.privacyStatement}
                    >
                      Huidige Privacy Statement (klik om te downloaden)
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Pakbon</label>
              </div>

              <div className="col-12 col-sm-8">
                <div style={{ paddingLeft: '1em' }}>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      value="no"
                      checked={this.state.sendPackageSlip === false}
                      name="sendPackageSlip"
                      id="sendPackageSlipFalse"
                      onChange={(e) => this.setState({ sendPackageSlip: e.target.value === 'yes' })}
                    />
                    <label className="form-check-label" htmlFor="sendPackageSlipFalse">
                      Niet meesturen
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      value="yes"
                      checked={this.state.sendPackageSlip === true}
                      name="sendPackageSlip"
                      id="sendPackageSlipTrue"
                      onChange={(e) => this.setState({ sendPackageSlip: e.target.value === 'yes' })}
                    />
                    <label className="form-check-label" htmlFor="sendPackageSlipTrue">
                      Meesturen met opdracht tot levering
                    </label>
                  </div>

                  {this.state.sendPackageSlip !== true ? null : (
                    <div className="form-input">
                      <br />
                      <textarea
                        className="form-control"
                        rows="5"
                        placeholder="Extra instructie op pakbon"
                        value={this.state.packageSlipExtraText}
                        onChange={(e) => this.setState({ packageSlipExtraText: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row subsection">
              <div className="col-12 col-sm-4">
                <label className="form-label">Extra CSS</label>
              </div>

              <div className="col-12 col-sm-8">
                <div style={{ paddingLeft: '1em' }}>
                  <Editor
                    value={this.state.mixinCss}
                    onValueChange={(mixinCss) => this.setState({ mixinCss })}
                    highlight={(mixinCss) => highlight(mixinCss, languages.css)}
                    padding={10}
                    placeholder="Schrijf hier je CSS..."
                    style={{
                      fontFamily: '"Fira code", "Fira Mono", monospace',
                      fontSize: 12,
                      border: '1px solid #ccc',
                      minHeight: '300px',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row section-buttons">
          <div className="col-12 text-align-right">
            <button
              className="btn btn-sm btn-secondary btn-rounded"
              onClick={() => this.saveAndNext(index)}
            >
              Volgende &raquo;
            </button>
          </div>
        </div>
      </section>
    )
  }
}



// WEBPACK FOOTER //
// ./ui/projects/create/settings-branding.js